/* Onesto cart drawer helpers (loaded with the drawer in layout/theme.liquid + popup.liquid):
   1. Open the drawer after every add to cart. The theme's <product-form> only fires a non-bubbling
      "add-to-cart" event and leaves opening to section-main-product.js, which the Onesto sections
      don't load - a capture listener on document catches the event from any product form.
   2. Header bag badge: hides itself while the count is 0 (component-cart.js / product-form only
      rewrite the [data-header-cart-count] text).
   3. Weight chips: switching a chip swaps the line to that variant. Shopify has no "change variant"
      call, so the new variant is added first (same quantity and properties) and the old line removed
      only after that succeeded - a failed add never loses the item. The drawer is then refreshed
      through the theme's window.refreshCart() (component-cart.js). */
(() => {
  if (window.onestoCartReady) return;
  window.onestoCartReady = true;

  // 1. Open the cart drawer after adding to cart (not on the cart page, where there's no drawer flow)
  document.addEventListener('add-to-cart', () => {
    if (document.body.classList.contains('template-cart')) return;
    // run after the theme's own listeners (main product / quick buy) so we never double-open
    setTimeout(() => {
      const drawer = document.getElementById('site-cart-sidebar');
      if (drawer && typeof drawer.show === 'function' && !drawer.opened) drawer.show();
    }, 0);
  }, true);

  // 1b. Direct add from a product card ([data-onesto-direct-add] = variant id), e.g. the Entdecker-Set
  //     on the shop page: add 1, then refresh + open the drawer through the theme's refreshCart().
  //     The link keeps its product URL as fallback (no JS, modifier clicks, failed add).
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-onesto-direct-add]');
    if (!button) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (button.classList.contains('is-working')) return;

    button.classList.add('is-working');
    button.setAttribute('aria-busy', 'true');
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.onestoDirectAdd), quantity: 1 }] })
      });
      if (!response.ok) throw new Error(response.status);
      if (typeof window.refreshCart === 'function') {
        window.refreshCart(); // re-renders the drawer + header counts and opens the drawer
      } else {
        window.location.href = (window.KROWN && KROWN.settings.routes.cart_url) || `${root}cart`;
      }
    } catch (error) {
      // e.g. sold out in the meantime - the product page shows why
      window.location.href = button.getAttribute('href');
    } finally {
      button.classList.remove('is-working');
      button.removeAttribute('aria-busy');
    }
  });

  // 2. Header badge visibility follows the live count
  const syncBadges = () => {
    document.querySelectorAll('[data-onesto-cart-badge]').forEach((badge) => {
      badge.hidden = (parseInt(badge.textContent, 10) || 0) === 0;
    });
  };
  const watchBadges = () => {
    syncBadges();
    document.querySelectorAll('[data-onesto-cart-badge]').forEach((badge) => {
      new MutationObserver(syncBadges).observe(badge, { childList: true, characterData: true, subtree: true });
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchBadges);
  } else {
    watchBadges();
  }

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  const showError = (form, message) => {
    if (!form) return;
    const existing = form.querySelector('.alert');
    if (existing) existing.remove();
    const alert = document.createElement('span');
    alert.className = 'alert alert--error';
    alert.textContent = message;
    form.prepend(alert);
  };

  document.addEventListener('click', async (event) => {
    const chip = event.target.closest('[data-cart-variant]');
    if (!chip || chip.disabled || chip.classList.contains('is-selected')) return;

    const item = chip.closest('[data-js-cart-item]');
    const cartForm = document.getElementById('AjaxCartForm');
    const form = cartForm ? cartForm.querySelector('form') : null;
    if (!item) return;

    event.preventDefault();
    if (form) form.classList.add('processing');
    const chips = Array.from(item.querySelectorAll('[data-cart-variant]'));
    const wasDisabled = chips.map((button) => button.disabled);
    chips.forEach((button) => { button.disabled = true; });

    try {
      const cart = await fetch(`${root}cart.js`, { headers: jsonHeaders }).then((r) => r.json());
      const line = cart.items.find((entry) => entry.key === item.dataset.id);
      if (!line) throw new Error('line not found');

      const addItem = { id: Number(chip.dataset.cartVariant), quantity: line.quantity };
      if (line.properties && Object.keys(line.properties).length) addItem.properties = line.properties;
      if (line.selling_plan_allocation) addItem.selling_plan = line.selling_plan_allocation.selling_plan.id;

      const added = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ items: [addItem] })
      });
      if (!added.ok) {
        const error = await added.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'add failed');
      }

      await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ id: line.key, quantity: 0 })
      });

      if (typeof window.refreshCart === 'function') {
        window.refreshCart();
      } else {
        window.location.reload();
      }
    } catch (error) {
      const fallback = window.KROWN && KROWN.settings && KROWN.settings.locales
        ? KROWN.settings.locales.cart_general_error
        : 'Error';
      showError(form, typeof error.message === 'string' && error.message !== 'add failed' && error.message !== 'line not found'
        ? error.message
        : fallback);
      chips.forEach((button, i) => { button.disabled = wasDisabled[i]; });
    } finally {
      if (form) form.classList.remove('processing');
    }
  });
})();
